import React, { useState, useEffect } from 'react';
import '../styles/ScienceCategory.css';

const ScienceCategory = () => {
  const [activeSlides, setActiveSlides] = useState({});

  const categories = [
    {
      id: 1,
      name: 'Chemistry',
      description: 'Explore the molecular world and reactions that shape matter. From atomic structures to complex organic compounds, chemistry unlocks the secrets of substances and their transformations.',
      features: ['Organic Chemistry', 'Inorganic Chemistry', 'Physical Chemistry', 'Lab Practicals'],
      images: [
        'https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=600&h=400&fit=crop',
      ],
      color: '#008374',
      order: 'left'
    },
    {
      id: 2,
      name: 'Physics',
      description: 'Understand the fundamental laws that govern the universe. From classical mechanics to quantum physics, explore motion, energy, forces, and the nature of reality itself.',
      features: ['Mechanics', 'Thermodynamics', 'Optics', 'Modern Physics'],
      images: [
        'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1518611505867-48a109c84a64?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1635070041078-e5b94fbb3d60?w=600&h=400&fit=crop',
      ],
      color: '#008374',
      order: 'right'
    },
    {
      id: 3,
      name: 'Biology',
      description: 'Discover the living world around us. Study cells, organisms, genetics, and ecosystems to understand how life evolves and adapts to its environment.',
      features: ['Cell Biology', 'Genetics', 'Ecology', 'Human Physiology'],
      images: [
        'https://images.unsplash.com/photo-1530836369250-ef72a3649cda?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1576091160550-112173f7f869?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1579154204601-01d5af3a1ad6?w=600&h=400&fit=crop',
      ],
      color: '#008374',
      order: 'left'
    },
    {
      id: 4,
      name: 'Mathematics',
      description: 'Master the language of numbers and logic. From algebra to calculus, develop problem-solving skills and explore the elegant patterns that underpin science and technology.',
      features: ['Algebra', 'Calculus', 'Geometry', 'Statistics'],
      images: [
        'https://images.unsplash.com/photo-1509228627152-72ae9e4b1122?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1596496181848-3091d4878b90?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop',
      ],
      color: '#008374',
      order: 'right'
    }
  ];

  useEffect(() => {
    const initialSlides = {};
    categories.forEach(cat => {
      initialSlides[cat.id] = 0;
    });
    setActiveSlides(initialSlides);
  }, []);

  const nextSlide = (categoryId, totalImages) => {
    setActiveSlides(prev => ({
      ...prev,
      [categoryId]: (prev[categoryId] + 1) % totalImages
    }));
  };

  const prevSlide = (categoryId, totalImages) => {
    setActiveSlides(prev => ({
      ...prev,
      [categoryId]: (prev[categoryId] - 1 + totalImages) % totalImages
    }));
  };

  return (
    <>
      {/* Banner Section */}
      <div className="scf-banner-wrapper">
        <div className="scf-banner-overlay"></div>
        <div className="scf-banner-content">
          <h1 className="scf-banner-title">SCIENCE</h1>
        </div>
      </div>

      {/* Main Science Section */}
      <section className="scf-science-container" id="scf-science-main">
       

        {/* Categories Grid */}
        <div className="scf-cards-grid">
          {categories.map((category, index) => (
            <div 
              key={category.id} 
              className={`scf-category-item scf-layout-${category.order}`}
              style={{ '--scf-primary-color': category.color }}
            >
              {/* Content Block */}
              <div className="scf-text-block">
                <h3 className="scf-topic-heading">{category.name}</h3>
                <div className="scf-accent-bar"></div>
                <p className="scf-topic-description">
                  {category.description}
                </p>
                
                <div className="scf-topics-section">
                  <h4 className="scf-topics-label">Key Topics:</h4>
                  <ul className="scf-topics-grid">
                    {category.features.map((feature, idx) => (
                      <li key={idx} className="scf-topic-item">
                        <span className="scf-bullet-point"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Image Carousel Block */}
              <div className="scf-image-block">
                <div className="scf-carousel-wrapper">
                  <div className="scf-slides-container">
                    {category.images.map((image, idx) => (
                      <div
                        key={idx}
                        className={`scf-image-slide ${idx === activeSlides[category.id] ? 'scf-visible' : ''}`}
                      >
                        <img src={image} alt={`${category.name} slide ${idx + 1}`} />
                      </div>
                    ))}
                  </div>

                  {/* Navigation Arrows */}
                  <button 
                    className="scf-nav-button scf-nav-left"
                    onClick={() => prevSlide(category.id, category.images.length)}
                    aria-label="Previous image"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </button>

                  <button 
                    className="scf-nav-button scf-nav-right"
                    onClick={() => nextSlide(category.id, category.images.length)}
                    aria-label="Next image"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>

                  {/* Slide Dots */}
                  <div className="scf-dots-container">
                    {category.images.map((_, idx) => (
                      <button
                        key={idx}
                        className={`scf-dot ${idx === activeSlides[category.id] ? 'scf-active-dot' : ''}`}
                        onClick={() => setActiveSlides(prev => ({ ...prev, [category.id]: idx }))}
                        aria-label={`Go to slide ${idx + 1}`}
                      ></button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default ScienceCategory;